import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { formatApiError, setPasswordRequest } from "@/lib/api";

/** Blocking overlay for a logged-in account that has no password yet (an
 *  auto-approved Customer member). They set one before using the app. */
export const SetPasswordGate = ({ onDone }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (password.length < 8) {
      toast.error("Use at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await setPasswordRequest(password);
      toast.success("Password set — you're all set!");
      onDone();
    } catch (error) {
      toast.error(formatApiError(error, "Could not set your password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid max-h-[100dvh] place-items-center overflow-y-auto bg-brand-canvas/95 px-4 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] backdrop-blur">
      <div className="w-full max-w-[420px] rounded-[20px] border border-black/5 bg-white/90 p-7 shadow-xl">
        <div className="mb-5 flex items-center gap-3">
          <img src="/logo-mark.png" alt="" className="h-11 w-11" />
          <div className="leading-[1.08]">
            <span className="block text-[15px] font-bold uppercase tracking-[0.08em] text-brand-ink">
              Greentek
            </span>
            <span className="block text-[15px] font-bold uppercase tracking-[0.08em] text-brand-gold">
              Alliance
            </span>
          </div>
        </div>
        <h1 className="brand-title text-2xl font-semibold text-brand-ink">
          Set your password
        </h1>
        <p className="mt-1.5 text-sm text-brand-muted">
          Welcome aboard! Choose a password to secure your account — you'll use it
          to sign in next time.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-pw" className="text-[13px] font-medium text-brand-ink">
              New password
            </Label>
            <Input
              id="new-pw"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="h-11 rounded-[12px] border-black/10 bg-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-pw" className="text-[13px] font-medium text-brand-ink">
              Confirm password
            </Label>
            <Input
              id="confirm-pw"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              className="h-11 rounded-[12px] border-black/10 bg-white"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="h-11 w-full rounded-[12px] bg-brand-forest font-semibold text-white"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Set password & continue"}
          </Button>
        </form>
      </div>
    </div>
  );
};
